import type {CrossSaleGroup, EnrichedCrossSalePart} from '../types';

const UNCATEGORIZED_GROUP_ID = 'uncategorized';

function hasRelationTypeMetadata(parts: EnrichedCrossSalePart[]): boolean {
    return parts.some(
        (part) => part.relationTypeId != null
            || Boolean(part.relationTypeTitle?.trim()?.length),
    );
}

function groupKey(part: EnrichedCrossSalePart): string {
    if (part.relationTypeId != null) {
        return `id:${part.relationTypeId}`;
    }
    const title = part.relationTypeTitle?.trim();
    if (title) {
        return `title:${title}`;
    }
    return UNCATEGORIZED_GROUP_ID;
}

function groupLabel(part: EnrichedCrossSalePart): string | undefined {
    return part.relationTypeTitle?.trim() || undefined;
}

/**
 * Groups cross-sale parts by relation type when the API provides relation metadata.
 * Otherwise returns a single flat group and hides category tabs in the UI.
 */
export function groupCrossSalesByRelation(parts: EnrichedCrossSalePart[]): {
    hasCategories: boolean;
    groups: CrossSaleGroup[];
} {
    if (!parts.length) {
        return {hasCategories: false, groups: []};
    }

    if (!hasRelationTypeMetadata(parts)) {
        return {
            hasCategories: false,
            groups: [{id: 'all', parts}],
        };
    }

    const grouped = parts.reduce<Record<string, CrossSaleGroup>>((acc, part) => {
        const key = groupKey(part);
        if (!acc[key]) {
            acc[key] = {
                id: key,
                relationTypeId: part.relationTypeId,
                relationTypeTitle: groupLabel(part),
                parts: [],
            };
        }
        acc[key].parts.push(part);
        return acc;
    }, {});

    const groups = Object.values(grouped).sort((a, b) => {
        const titleA = a.relationTypeTitle ?? '';
        const titleB = b.relationTypeTitle ?? '';
        return titleA.localeCompare(titleB);
    });

    return {
        hasCategories: groups.length > 1,
        groups,
    };
}
